import type { User } from '../../../authentication/types/types.auth';
import {
  Edit,
  ThumbDown,
  ThumbUp,
  TrendingUp,
  Visibility,
} from '@mui/icons-material';
import {
  Avatar,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useNavigate } from 'react-router-dom';

// Minimal user type for ProfileHeader
interface ProfileHeaderUser {
  id?: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  avatarColor?: string;
  tagLine?: string;
  reviews?: unknown[];
}

interface ProfileHeaderProps {
  user: User | ProfileHeaderUser;
  isEditing: boolean;
  reactionStats: {
    totalLikes: number;
    totalDislikes: number;
    loading: boolean;
  };
  onEditClick?: () => void;
  avatarColor?: string;
  totalReviews?: number;
}

const getAvatarInitials = (user: ProfileHeaderUser): string => {
  if (user.username) {
    const words = user.username.match(/[A-Z][a-z]+/g) || [];
    if (words.length >= 2) {
      return (words[0]?.[0] || '') + (words[1]?.[0] || '');
    }
    return user.username.substring(0, 2).toUpperCase();
  }
  return (user.firstName?.[0] || '') + (user.lastName?.[0] || '');
};

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  user,
  isEditing,
  reactionStats,
  onEditClick,
  avatarColor,
  totalReviews,
}) => {
  const navigate = useNavigate();

  const handleViewProfile = () => {
    navigate(`/user/${user.username}`);
  };

  const stats = [
    {
      icon: <ThumbUp htmlColor="#86efac" />,
      label: 'Helpful Reviews',
      value: reactionStats.loading
        ? <CircularProgress size={20} sx={{ color: 'white' }} />
        : reactionStats.totalLikes,
    },
    {
      icon: <ThumbDown htmlColor="#fca5a5" />,
      label: 'Not Helpful',
      value: reactionStats.loading
        ? <CircularProgress size={20} sx={{ color: 'white' }} />
        : reactionStats.totalDislikes,
    },
    {
      icon: <TrendingUp htmlColor="#fcd34d" />,
      label: 'Published Reviews',
      value: totalReviews !== undefined ? totalReviews : user.reviews?.length || 0,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      data-testid="profile-header"
      className="profile-header"
    >
      <div className="profile-header__inner">
        <div className="profile-header__lede">
          <Avatar
            className="profile-header__avatar"
            sx={{ bgcolor: avatarColor || user.avatarColor || '#0b74da' }}
          >
            {getAvatarInitials(user)}
          </Avatar>

          <div className="profile-header__identity">
            <h2 className="profile-header__name">
              {user.username || `${user.firstName} ${user.lastName}`}
            </h2>
            {user.tagLine && (
              <p className="profile-header__tagline">{user.tagLine}</p>
            )}
          </div>

          {!isEditing && onEditClick && (
            <div className="profile-header__actions">
              <Tooltip title="View Public Profile">
                <IconButton
                  className="profile-header__action"
                  onClick={handleViewProfile}
                >
                  <Visibility />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Profile">
                <IconButton
                  className="profile-header__action"
                  onClick={onEditClick}
                  data-testid="edit-profile-button"
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>

        <div className="profile-header__stats">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="profile-header__stat"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index, duration: 0.4 }}
            >
              <div className="profile-header__stat-icon">{stat.icon}</div>
              <div className="profile-header__stat-value">{stat.value}</div>
              <div className="profile-header__stat-label">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};
